// شاشةٌ صغيرة تظهر فور وصول أي إشعار (قرار المالك).
//
// «عاوزه كمان تظهرله شاشة صغيرة مفاجأة وهو قاعد بالخصم أو بالمكافأة أو بالتحذير أو
// التنبيه — أي نوع من أنواع الإشعارات تظهر شاشة مفاجأة مع الإشعارات».
//
// **ولماذا لا تُغلق نفسها**: الخصم والتحذير ممّا يجب أن يُقرأ لا أن يمرّ. ولو اختفت
// بعد ثوانٍ لَمرّت على من كان ينظر إلى ورقةٍ أمامه — وهو عين من قُصد بها. فتبقى حتى
// يغلقها بيده، وإغلاقُها **يسمها مقروءة** فلا تُعاد عليه.
//
// **ولا تحجب الشاشة**: بطاقةٌ في ركنٍ لا نافذةٌ مُعتِمة — فمن كان في منتصف حجزٍ لا
// يُقطع عليه عمله. وحدُّها الأعلى ثلاثٌ معًا: ما زاد ينتظر في الجرس.
//
// **ولا تظهر عند أول فتحٍ للجلسة**: ما كان قائمًا قبل أن يفتح ليس «جديدًا» — وإلّا
// قفزت عليه إشعارات أسبوعٍ مضى كلّما سجّل دخوله.

import { createPortal } from 'react-dom';
import type { AppNotification } from '../lib/api.ts';
import { cairoDateAr } from '../lib/format.ts';

/** لونُ البطاقة ولافتتُها بحسب شدّة الإشعار — تُعرف قبل أن تُقرأ. */
const TONE: Record<string, { box: string; chip: string; label: string; icon: string }> = {
  CRITICAL: { box: 'border-rose-400 bg-rose-50', chip: 'bg-rose-600 text-white', label: 'مهم', icon: '⚠️' },
  WARNING: { box: 'border-rose-300 bg-rose-50', chip: 'bg-rose-600 text-white', label: 'تنبيه', icon: '⚠️' },
  INFO: { box: 'border-brand-300 bg-brand-50', chip: 'bg-brand-600 text-white', label: 'إشعار', icon: '🔔' },
};

/** ما يُفهم من نوع الإشعار — كلمةٌ فوق العنوان تقول «ده إيه». */
const KIND_AR: Record<string, string> = {
  ADMIN_DEDUCTION: 'خصم',
  ADMIN_DEDUCTION_REVERSAL: 'ردّ خصم',
  ATTENDANCE_DEDUCTION: 'خصم حضور',
  ATTENDANCE_WARNING: 'تنبيه حضور',
  ANNUAL_BONUS_REMINDER: 'مكافأة',
  ADMIN_ANNOUNCEMENT: 'إعلان',
  WEB_LEAD: 'طلب من الموقع',
  CHAT_WAITING: 'شات الموقع',
  WEBSITE_WEEKLY_REPORT: 'تقرير الموقع',
};

export function NotificationPopup({ items, onClose }: {
  items: AppNotification[];
  onClose: (n: AppNotification) => void;
}) {
  if (items.length === 0) return null;

  /**
   * **يُرسَم في جسم الصفحة لا في مكانه من الشجرة** (قرار المالك 22/09/2026: «ليه الإعلان
   * ده فوق كده — اظبطه»).
   *
   * فالجرسُ يعيش في الترويسة، والترويسةُ عليها `backdrop-blur` — و`backdrop-filter`
   * تجعل العنصرَ **كتلةً حاويةً** لكل `position: fixed` تحته. فـ«ثابتٌ في أسفل
   * الشاشة» صارت «أسفلَ الترويسة»، فتظهر التنبيهات فوق الصفحة لا تحتها.
   *
   * والبوّابة (portal) تُخرجه من تحتها إلى `document.body` — فيعود «أسفل الشاشة»
   * يعني أسفلَ الشاشة. ولا يتغيّر شيءٌ في منطقه ولا في عمر الحالة: البوّابة موضعُ رسمٍ
   * لا موضعُ شجرةٍ في React.
   */
  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-3 sm:inset-x-auto sm:start-4 sm:bottom-4 sm:items-start sm:p-0">
      {items.slice(0, 3).map(n => {
        const t = TONE[n.severity] ?? TONE.INFO;
        return (
          <div key={n.id} role="alert"
            className={`pointer-events-auto w-full max-w-md animate-[popIn_.18s_ease-out] rounded-xl border-2 ${t.box} p-4 shadow-lg`}>
            <div className="flex items-start gap-2">
              <span aria-hidden="true" className="text-xl leading-none">{t.icon}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${t.chip}`}>{t.label}</span>
                  {KIND_AR[n.type] && (
                    <span className="rounded-full bg-white/70 px-2 py-0.5 text-xs font-medium text-ink-700">
                      {KIND_AR[n.type]}
                    </span>
                  )}
                </div>
                <div className="mt-1.5 text-base font-bold text-ink-900">{n.title}</div>
                {n.body && <p className="mt-1 whitespace-pre-line text-sm leading-7 text-ink-700">{n.body}</p>}
                <div className="mt-1.5 text-xs tabular-nums text-ink-500">
                  {cairoDateAr(n.createdAt)}
                </div>
              </div>
            </div>
            <button type="button" className="btn-ghost mt-3 w-full py-2 text-sm font-semibold"
              onClick={() => onClose(n)}>
              تمام، قريتها
            </button>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
